import { getConditionalArray, getConditionalObject } from 'utils/data';

export const getMuiButtonOverrides = ({ theme: { shape, palette } }) => {
  const isDark = palette.mode === 'dark';

  // Disabled contained in dark: the bright fill at opacity 0.4 reads as a muddy slate block with
  // dim text (the user-reported look). Replace it with a legible ghost — a subtle surface fill,
  // faint border, and dimmed-but-readable label. Light mode keeps the global opacity:0.4.
  const darkDisabledContainedStyles = getConditionalObject(isDark, () => ({
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: `${palette.background.secondary} !important`,
      color: `${palette.text.disabled} !important`,
      border: `1px solid ${palette.border.light}`,
    },
  }));

  // Disabled outlined in dark: keep the outline shape but dim border + label so it stays legible
  // (the global opacity:0.4 fades the accent border to near-invisible on the dark canvas).
  const darkDisabledOutlinedStyles = getConditionalObject(isDark, () => ({
    '&.Mui-disabled': {
      opacity: 1,
      color: `${palette.text.disabled} !important`,
      border: `1px solid ${palette.border.light} !important`,
    },
  }));

  const variantsPerColor = Object.entries(palette).reduce(
    (acc, [colorName, { main, light, dark, soft }]) => {
      if (!main) {
        return acc;
      }

      const sharedStyles = {
        // Focus ring: in dark the pale 0.14-alpha soft tint is nearly invisible, so use the solid
        // accent for a visible ring; light keeps the existing subtle soft ring (byte-unchanged).
        '&:focus, &:focus-visible, &.Mui-focusVisible': {
          boxShadow: `0px 0px 0px 2px ${isDark ? main : soft}`,
        },

        '& .MuiTouchRipple-child': {
          backgroundColor: `${light} !important`,
        },
      };

      const variantsWithCurrentColor = [
        {
          props: { variant: 'contained', color: colorName },
          style: {
            backgroundColor: `${main} !important`,
            // Color will be always white or black depends on background color
            color: `${palette.getContrastText(main)} !important`,

            '&:hover': {
              backgroundColor: `${dark} !important`,
            },

            // An in-button CircularProgress defaults to color="primary", which paints its stroke from
            // theme.palette.primary.main — the SAME value as this button's own fill, making it
            // invisible (light: rgb(16, 104, 244) on rgb(16, 104, 244)). Force it to inherit the
            // button's own label color instead, so it always reads as the correct contrast foreground
            // in both modes and never drifts out of sync with the label-text fix above.
            '& .MuiCircularProgress-root': {
              color: 'inherit !important',
            },

            ...darkDisabledContainedStyles,
            ...sharedStyles,
          },
        },
        {
          props: { variant: 'outlined', color: colorName },
          style: {
            border: `1px solid ${main} !important`,
            color: `${main} !important`,
            // Surface token (white in light, dark canvas in dark) — was a hardcoded white literal
            // that rendered outlined buttons pure-white on the dark canvas.
            backgroundColor: `${palette.background.primary} !important`,

            '&:hover': {
              backgroundColor: `${soft} !important`,
            },

            ...darkDisabledOutlinedStyles,
            ...sharedStyles,
          },
        },
        ...getConditionalArray(colorName === 'error', [
          {
            props: { variant: 'line', color: colorName },
            style: {
              color: `${main} !important`,

              '&:hover': {
                backgroundColor: `${soft} !important`,
              },

              ...sharedStyles,
            },
          },
        ]),
      ];

      acc.push(...variantsWithCurrentColor);

      return acc;
    },
    [],
  );

  return {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: shape.radius.l,
    boxShadow: 'none',
    textTransform: 'none',

    '&:hover': {
      boxShadow: 'none',
    },

    '&.Mui-disabled': {
      opacity: 0.4,
    },

    variants: variantsPerColor,
  };
};
